import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { authReady } from './lib/auth';
import { pageScroller } from './lib/scroll';
import { Focus } from './routes/Focus';
import { RouteError } from './routes/RouteError';
import { Shell } from './routes/Shell';
import { Home } from './routes/Home';

const rootRoute = createRootRoute({
  component: Outlet,
  // A wrong address is rare: its page loads when it's needed.
  notFoundComponent: lazyRouteComponent(() => import('./routes/NotFound'), 'NotFound'),
  errorComponent: RouteError,
});

function mapSearch(s: Record<string, unknown>): {
  cluster?: string;
  select?: string;
  node?: string;
  track?: string;
  books?: string;
} {
  const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined);
  return {
    ...(str(s.cluster) ? { cluster: str(s.cluster) } : {}),
    ...(str(s.select) ? { select: str(s.select) } : {}),
    ...(str(s.node) ? { node: str(s.node) } : {}),
    // Your map only: the track whose books it shows, and some of those books, their ids separated by commas.
    ...(str(s.track) ? { track: str(s.track) } : {}),
    ...(str(s.books) ? { books: str(s.books) } : {}),
  };
}

async function requireSession({ location }: { location: { href: string; pathname: string } }) {
  const s = await authReady();
  if (s.status === 'signed-in') return;
  // Someone arriving at the front door sees what wellread is; a deep link asks them to sign in and comes back.
  if (location.pathname === '/') throw redirect({ to: '/welcome' });
  throw redirect({ to: '/sign-in', search: { next: location.href } });
}

// The public front page. Signed in, it's home instead.
const welcome = createRoute({
  getParentRoute: () => rootRoute,
  path: '/welcome',
  beforeLoad: async () => {
    const s = await authReady();
    if (s.status === 'signed-in') throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/Welcome'), 'Welcome'),
});

const signIn = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  // mode=signup words the page for someone new; the email code is the same either way.
  // error (and via, the provider) say why a Google or Apple sign-in came back without the reader.
  validateSearch: (
    s: Record<string, unknown>,
  ): { next?: string; mode?: 'signup'; error?: string; via?: 'google' | 'apple' } => ({
    ...(typeof s.next === 'string' ? { next: s.next } : {}),
    ...(s.mode === 'signup' ? { mode: 'signup' as const } : {}),
    ...(typeof s.error === 'string' && /^[a-z_]{1,40}$/.test(s.error) ? { error: s.error } : {}),
    ...(s.via === 'google' || s.via === 'apple' ? { via: s.via } : {}),
  }),
  beforeLoad: async ({ search }) => {
    const s = await authReady();
    if (s.status === 'signed-in') throw redirect({ to: safeNext(search.next) });
  },
  // A signed-in reader never needs this page's code on the way in.
  component: lazyRouteComponent(() => import('./routes/SignIn'), 'SignIn'),
});

// Where Google and Apple come back to, signed in by their callback (or with an error).
const signInDone = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in/done',
  component: lazyRouteComponent(() => import('./routes/SignIn'), 'SignInDone'),
});

// The privacy policy and the terms: public, whoever is signed in, since the
// sign-in screen, the front page and Google's consent screen link to them.
const privacy = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: lazyRouteComponent(() => import('./routes/Legal'), 'Privacy'),
});
const terms = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: lazyRouteComponent(() => import('./routes/Legal'), 'Terms'),
});

// Everything under the top bar.
const app = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: requireSession,
  component: Shell,
});
const home = createRoute({ getParentRoute: () => app, path: '/', component: Home });
const library = createRoute({
  getParentRoute: () => app,
  path: '/library',
  component: lazyRouteComponent(() => import('./routes/Library'), 'Library'),
});
// A track: what it is for, how it is going and its books. "about" and "new" come first so they aren't read as ids.
const tracksAbout = createRoute({
  getParentRoute: () => app,
  path: '/tracks/about',
  component: lazyRouteComponent(() => import('./routes/TracksAbout'), 'TracksAbout'),
});
const newTrack = createRoute({
  getParentRoute: () => app,
  path: '/tracks/new',
  // book: the new book this track is being made for, from the question of its track; the reader goes back to it.
  validateSearch: (s: Record<string, unknown>): { book?: string } =>
    typeof s.book === 'string' && s.book ? { book: s.book } : {},
  component: lazyRouteComponent(() => import('./routes/Track'), 'NewTrack'),
});
const track = createRoute({
  getParentRoute: () => app,
  path: '/tracks/$trackId',
  component: lazyRouteComponent(() => import('./routes/Track'), 'Track'),
});
// Today and the review page are gone; an old link lands on home.
const today = createRoute({
  getParentRoute: () => app,
  path: '/today',
  beforeLoad: () => {
    throw redirect({ to: '/' });
  },
});
const review = createRoute({
  getParentRoute: () => app,
  path: '/review',
  beforeLoad: () => {
    throw redirect({ to: '/' });
  },
});
const importRoute = createRoute({
  getParentRoute: () => app,
  path: '/import',
  component: lazyRouteComponent(() => import('./routes/Import'), 'Import'),
});
// A book: its header and tab row, with the selected tab under them. The tabs
// are child routes, so moving between them never remounts the header.
export const bookRoute = createRoute({
  getParentRoute: () => app,
  path: '/books/$bookId',
  // onto: the track the book was added from, where the question of which track starts.
  // analyse: a book kept only to read, asked which track it is for after all.
  validateSearch: (s: Record<string, unknown>): { onto?: string; analyse?: true } => ({
    ...(typeof s.onto === 'string' && s.onto ? { onto: s.onto } : {}),
    ...(s.analyse === true || s.analyse === 'true' ? { analyse: true as const } : {}),
  }),
  component: lazyRouteComponent(() => import('./routes/Book'), 'Book'),
});
const overviewRoute = createRoute({
  getParentRoute: () => bookRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./routes/Overview'), 'Overview'),
});
// A technical book's concept map: its themes, which you can go inside. cluster: the level
// shown; select: what's picked on it; node: land on the level holding that idea.
export const graphRoute = createRoute({
  getParentRoute: () => bookRoute,
  path: 'graph',
  validateSearch: mapSearch,
  component: lazyRouteComponent(() => import('./routes/KnowledgeMap'), 'BookMap'),
});
export const castRoute = createRoute({
  getParentRoute: () => bookRoute,
  path: 'cast',
  validateSearch: (s: Record<string, unknown>): { node?: string } =>
    typeof s.node === 'string' ? { node: s.node } : {},
  component: lazyRouteComponent(() => import('./routes/Cast'), 'Cast'),
});
export const notesRoute = createRoute({
  getParentRoute: () => bookRoute,
  path: 'notes',
  component: lazyRouteComponent(() => import('./routes/Notes'), 'Notes'),
});
export const storyRoute = createRoute({
  getParentRoute: () => bookRoute,
  path: 'story',
  component: lazyRouteComponent(() => import('./routes/Story'), 'Story'),
});

// Your map: the ideas of every technical book, grouped into themes across books.
const map = createRoute({
  getParentRoute: () => app,
  path: '/map',
  validateSearch: mapSearch,
  component: lazyRouteComponent(() => import('./routes/KnowledgeMap'), 'YourMap'),
});

// Explore: a question about your books, the recent conversations, and the reading list.
const explore = createRoute({
  getParentRoute: () => app,
  path: '/explore',
  component: lazyRouteComponent(() => import('./routes/Explore'), 'Explore'),
});
// One conversation in Explore. The client names it: the first question creates it.
const conversation = createRoute({
  getParentRoute: () => app,
  path: '/explore/c/$conversationId',
  component: lazyRouteComponent(() => import('./routes/Conversation'), 'Conversation'),
});
// The interests picker is gone; an old link lands on Explore itself.
const exploreInterests = createRoute({
  getParentRoute: () => app,
  path: '/explore/interests',
  beforeLoad: () => {
    throw redirect({ to: '/explore' });
  },
});

// Notes from every book. A book's own notes are a tab on the book.
const allNotes = createRoute({
  getParentRoute: () => app,
  path: '/notes',
  component: lazyRouteComponent(() => import('./routes/AllNotes'), 'AllNotes'),
});

const settingsTabs = ['reading', 'account', 'books', 'credits'] as const;
export type SettingsTab = (typeof settingsTabs)[number];
// Earlier section names, so an old link still lands somewhere sensible.
const settingsAliases: Record<string, SettingsTab> = {
  study: 'reading',
  profile: 'account',
  security: 'account',
  usage: 'credits',
};
const settingsIndex = createRoute({
  getParentRoute: () => app,
  path: '/settings',
  beforeLoad: () => {
    throw redirect({ to: '/settings/$tab', params: { tab: 'reading' } });
  },
});
export const settingsRoute = createRoute({
  getParentRoute: () => app,
  path: '/settings/$tab',
  params: {
    parse: (p: { tab: string }) => ({
      tab: (settingsTabs as readonly string[]).includes(p.tab)
        ? (p.tab as SettingsTab)
        : (settingsAliases[p.tab] ?? 'reading'),
    }),
    stringify: (p: { tab: SettingsTab }) => ({ tab: p.tab }),
  },
  component: lazyRouteComponent(() => import('./routes/Settings'), 'Settings'),
});

// Premium: what a paid month gives and the way to pay for one. A page of its
// own, without the top bar; its code and Razorpay's load when it is opened.
const premium = createRoute({
  getParentRoute: () => rootRoute,
  path: '/premium',
  beforeLoad: requireSession,
  component: lazyRouteComponent(() => import('./routes/Payment'), 'Premium'),
});

// Full-screen: the reader and the session. Loaded on demand.
const focus = createRoute({
  getParentRoute: () => rootRoute,
  id: 'focus',
  beforeLoad: requireSession,
  component: Focus,
});
export const readerRoute = createRoute({
  getParentRoute: () => focus,
  path: '/books/$bookId/read/$chapter',
  params: {
    parse: (p: { bookId: string; chapter: string }) => ({
      bookId: p.bookId,
      chapter: Math.max(0, Number.parseInt(p.chapter, 10) || 0),
    }),
    stringify: (p: { bookId: string; chapter: number }) => ({ bookId: p.bookId, chapter: String(p.chapter) }),
  },
  // at: open at this offset; end: a citation's end, marked; last: open on the chapter's last page.
  validateSearch: (s: Record<string, unknown>): { at?: number; end?: number; last?: number } => ({
    ...(typeof s.at === 'number' ? { at: s.at } : {}),
    ...(typeof s.end === 'number' ? { end: s.end } : {}),
    ...(s.last === 1 ? { last: 1 } : {}),
  }),
  component: lazyRouteComponent(() => import('./routes/Reader'), 'Reader'),
});
export const sessionRoute = createRoute({
  getParentRoute: () => focus,
  path: '/review/session',
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    book?: string;
    focus?: string;
    track?: string;
    cluster?: string;
    books?: string;
    anyway?: boolean;
  } => ({
    ...(typeof s.book === 'string' ? { book: s.book } : {}),
    ...(typeof s.focus === 'string' ? { focus: s.focus } : {}),
    ...(typeof s.track === 'string' ? { track: s.track } : {}),
    // One theme of a map: only the ideas inside it.
    ...(typeof s.cluster === 'string' ? { cluster: s.cluster } : {}),
    // Some of a track's books, as its map showed them: their ids separated by commas.
    ...(typeof s.books === 'string' && s.books ? { books: s.books } : {}),
    // Asked for with nothing waiting: ideas answered and not due yet come too.
    ...(s.anyway === true ? { anyway: true } : {}),
  }),
  component: lazyRouteComponent(() => import('./routes/Session'), 'Session'),
});

/**
 * The component gallery, dev only. It is the surface design works against
 * without touching React, and what the visual-diff test screenshots. Kept off
 * the auth shell so it needs no session, and out of production builds.
 */
const gallery = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dev/gallery',
  component: lazyRouteComponent(() => import('./routes/Gallery'), 'Gallery'),
});

const routeTree = rootRoute.addChildren([
  welcome,
  signIn,
  signInDone,
  privacy,
  terms,
  app.addChildren([
    home,
    library,
    tracksAbout,
    newTrack,
    track,
    today,
    review,
    importRoute,
    bookRoute.addChildren([overviewRoute, graphRoute, castRoute, notesRoute, storyRoute]),
    allNotes,
    explore,
    conversation,
    exploreInterests,
    map,
    settingsIndex,
    settingsRoute,
  ]),
  focus.addChildren([readerRoute, sessionRoute]),
  premium,
  ...(import.meta.env.DEV ? [gallery] : []),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent', // prefetch data and code on link hover
  defaultPreloadStaleTime: 0,
  // The reader puts the page at the reading position itself; the router's
  // restoring would carry one chapter's scroll into the next.
  scrollRestoration: ({ location }) => !/^\/books\/[^/]+\/read\//.test(location.pathname),
  // Every page opens at the top. Left to itself the router carries the last
  // page's scroll into the next one, since both scroll in the same place;
  // Back and Forward still return to where the reader was.
  scrollToTopSelectors: [pageScroller],
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
  interface HistoryState {
    /** Home was arrived at from a payment that went through: it says so. */
    paid?: boolean;
    /** A track's page, opened on the track just made: it opens with the books suggested for it. */
    suggest?: boolean;
  }
}

/** Only same-origin paths are allowed as a post-sign-in destination. */
export function safeNext(next?: string): string {
  if (!next) return '/';
  try {
    const u = new URL(next, window.location.origin);
    return u.origin === window.location.origin &&
      !u.pathname.startsWith('/sign-in') &&
      !u.pathname.startsWith('/welcome')
      ? u.pathname + u.search
      : '/';
  } catch {
    return '/';
  }
}
